import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { emailValidator, noWhitespaceValidator } from '@core/common-legacy';
import {
  existingAppMemberValidator,
  existingTellentMemberValidator,
  TellentMembersHttpClient,
} from '@core/tellent-data';
import { TellentAccessService } from '@core/tellent-logic';
import { InviteMethod, LoginType, MemberFormField } from '@core/tellent-user-invitation-types';

@Injectable()
export class MemberDetailsFormService {
  private readonly tellentAccessService = inject(TellentAccessService);
  private readonly tellentMembersHttpClient = inject(TellentMembersHttpClient);
  private readonly formBuilder = inject(FormBuilder);

  public createMemberFormGroup(
    inviteMethod: InviteMethod,
    loginType?: LoginType,
  ): FormGroup<MemberFormField<{}>> {
    const memberFormGroup = this.formBuilder.group({
      email: [{ value: '', disabled: true }],
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      login: [{ value: '', disabled: true }],
    });

    this.updateMemberGroup(memberFormGroup, inviteMethod, loginType);

    return memberFormGroup;
  }

  public updateMemberGroup(group: any, inviteMethod: string, loginType?: string): void {
    const email: FormControl = group.get('email');
    const firstName: FormControl = group.get('firstName');
    const lastName: FormControl = group.get('lastName');
    const login: FormControl = group.get('login');

    // Reset all controls
    email.disable();
    email.clearValidators();
    firstName.disable();
    firstName.clearValidators();
    lastName.disable();
    lastName.clearValidators();
    login.disable();
    login.clearValidators();

    switch (inviteMethod) {
      case InviteMethod.Email:
        // STATE 1: Invite via email
        login.reset('');
        email.enable();
        email.setValidators([Validators.required, emailValidator]);
        email.setAsyncValidators(this.resolveEmailAsyncValidator());
        firstName.enable();
        firstName.setValidators(noWhitespaceValidator);
        lastName.enable();
        firstName.setValidators(noWhitespaceValidator);
        break;

      case InviteMethod.Login:
        email.reset('');
        // STATE 2: Generate login
        switch (loginType) {
          case LoginType.Auto:
            // STATE 2.1: Generate login automatically
            firstName.enable();
            firstName.setValidators([Validators.required, noWhitespaceValidator]);
            firstName.updateValueAndValidity();
            lastName.enable();
            lastName.setValidators([Validators.required, noWhitespaceValidator]);
            lastName.updateValueAndValidity();
            break;

          case LoginType.Custom:
            // STATE 2.2: Custom login
            firstName.enable();
            firstName.setValidators(noWhitespaceValidator);
            lastName.enable();
            lastName.setValidators(noWhitespaceValidator);
            login.enable();
            login.setValidators([Validators.required, noWhitespaceValidator]);
            break;

          default:
            break;
        }
        break;

      default:
        break;
    }

    group.updateValueAndValidity();
  }

  private resolveEmailAsyncValidator() {
    return this.tellentAccessService.canManageTellentUsers()
      ? existingTellentMemberValidator(this.tellentMembersHttpClient)
      : existingAppMemberValidator(this.tellentMembersHttpClient);
  }
}
